/**
 * Vendor quotes on a shortlisted product, and the homeowner approval.
 *
 * Separate from `projects-api.service.ts` because the shapes here are new rather
 * than migrated: quotes used to live as a JSONB array on the shortlist entry and
 * the approval as an index into it, which is the defect plan 007 exists to fix.
 */

import type { VendorRatingSummary } from "@shared/types";
import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1";

/**
 * A quote as the API serves it.
 *
 * `price` is paise. `id` matters more than it looks: the homeowner's approval
 * points at this id, which is what stops a reorder or a rewrite moving the
 * approval to a different vendor. There is deliberately no array index anywhere
 * in this file.
 */
export interface ShortlistQuote {
	id: string;
	vendorId: string;
	vendorName: string;
	vendorCity: string | null;
	vendorIsVerified: boolean;
	/** People's ratings of this vendor after use; see `VendorRatingSummary`. */
	vendorRating: VendorRatingSummary;
	price: number;
	uom: string;
	leadTimeDays: number | null;
	leadTimeNote: string | null;
	tag: string | null;
	notes: string | null;
	addedBy: string | null;
	/**
	 * The ROLE of whoever added it, rendered to the homeowner before they approve.
	 * A contractor may source from a vendor they profit from, and the user id
	 * alone discloses nothing on screen.
	 */
	addedByRole: "designer" | "contractor" | null;
	sortOrder: number;
}

export interface VendorIdentity {
	id: string;
	name: string;
	city: string | null;
	state: string | null;
	isVerified: boolean;
}

export interface SelectQuoteResult {
	shortlistId: string;
	selectedQuoteId: string;
	/** BOQ lines this approval repriced. They now need the homeowner's re-approval. */
	repricedEntryIds: string[];
	/** Set when the approval was recorded but the BOQ was not written, with why. */
	boqSkipped: null | { reason: string; message: string };
}

export async function listQuotes(
	projectId: string,
	shortlistId: string,
): Promise<ShortlistQuote[]> {
	const response = await apiClient.get(
		`${API_BASE}/projects/${projectId}/shortlist/${shortlistId}/quotes`,
	);
	return response.data.data.quotes ?? [];
}

export async function addQuote(
	projectId: string,
	shortlistId: string,
	input: {
		vendorId: string;
		price: number;
		uom: string;
		leadTimeDays?: number | null;
		leadTimeNote?: string | null;
		tag?: string | null;
		notes?: string | null;
	},
): Promise<ShortlistQuote> {
	const response = await apiClient.post(
		`${API_BASE}/projects/${projectId}/shortlist/${shortlistId}/quotes`,
		input,
	);
	return response.data.data.quote;
}

export async function deleteQuote(
	projectId: string,
	shortlistId: string,
	quoteId: string,
): Promise<void> {
	await apiClient.delete(
		`${API_BASE}/projects/${projectId}/shortlist/${shortlistId}/quotes/${quoteId}`,
	);
}

/**
 * The homeowner approves a quote.
 *
 * Takes a quote ID, never a position. Returns what actually reached the BOQ,
 * because the defect this replaces was an approval that silently did nothing —
 * so the caller can tell the user rather than leave them to assume.
 */
export async function selectQuote(
	projectId: string,
	shortlistId: string,
	quoteId: string,
): Promise<SelectQuoteResult> {
	const response = await apiClient.post(
		`${API_BASE}/projects/${projectId}/shortlist/${shortlistId}/select`,
		{ quoteId },
	);
	return response.data.data;
}

export async function findVendors(q: string): Promise<VendorIdentity[]> {
	// The API requires two characters, so asking earlier just wastes a request.
	if (q.trim().length < 2) return [];
	const response = await apiClient.get(`${API_BASE}/vendors`, {
		params: { q },
	});
	return response.data.data.vendors ?? [];
}

export async function createVendor(input: {
	name: string;
	city?: string | null;
}): Promise<{ vendor: VendorIdentity; nearMatches: VendorIdentity[] }> {
	const response = await apiClient.post(`${API_BASE}/vendors`, input);
	return {
		vendor: response.data.data.vendor,
		nearMatches: response.data.data.nearMatches ?? [],
	};
}
